/**
 * The Vera home page: a compact, sectioned launcher.
 *
 * Layout goals (Sasha, 2026-08-08 redesign):
 *   - a user with every module sees everything WITHOUT scrolling: compact
 *     hero, dense tiles, tight section rhythm;
 *   - sections are the admin-managed launcher categories
 *     (shell.launcherCategories via Catalog > Configuration), falling back
 *     to the code-level defaults in MODULE_CATALOG;
 *   - each section carries ONE approved brand color (guide 4.5) on its LABEL
 *     DOT, while every tile keeps its own locked module accent. Revised
 *     2026-08-08 (Sasha): the first pass painted every circle in a section
 *     with the category color, which made a section read as one flat block.
 *     The brand guide forbids custom tints and composites (only 10% opacity
 *     steps on secondaries, and Veracity Orange none at all), so there is no
 *     compliant way to vary hue WITHIN a category. Per-module accents are
 *     already approved, already distinct, and already what the AppShell
 *     header of each app uses, so the tile now matches the app it opens;
 *   - a few tiles show live stats (in-force groups + EE lives, new ideas,
 *     active initiatives) from one cached BFF call that never blocks paint.
 */

import { Link } from 'react-router-dom';
import {
  Display,
  Title3,
  Body1,
  Caption1,
  Card,
  Badge,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  ShieldKeyholeRegular,
  LockClosedRegular,
  ArrowUpRightRegular,
} from '@fluentui/react-icons';
import {
  veracityColors,
  isLightColor,
  useAccessibleModules,
  DaybreakMark,
  type AccessibleModule,
} from '@shared/ui';
import { useAuth } from '@shared/providers/AuthProvider';
import { hasGlobalAdmin, canManageDemoPages } from '@shared/auth';
import { useLauncherCategories, type LauncherCategoryConfig } from '@entities/admin-config';
import { useLauncherStats, statLineFor } from './useLauncherStats';

/** Time-of-day greeting — leans into the "Daybreak" brand motif. */
function greetingForNow(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

const useStyles = makeStyles({
  page: {
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    maxWidth: '1240px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  // --- Sunrise hero band (Daybreak motif), compact -------------------------
  hero: {
    position: 'relative',
    overflow: 'hidden',
    marginBottom: tokens.spacingVerticalL,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    borderRadius: '20px',
    backgroundColor: veracityColors.firstLight,
    backgroundImage: `radial-gradient(120% 140% at 88% 120%, ${veracityColors.veracityOrange}22 0%, ${veracityColors.daybreakAmber}14 30%, transparent 60%), linear-gradient(120deg, ${veracityColors.firstLight} 0%, ${veracityColors.gentleHaze} 100%)`,
    border: '1px solid #f0dcc4',
  },
  heroWatermark: {
    position: 'absolute',
    right: '-30px',
    bottom: '-52px',
    opacity: 0.12,
    pointerEvents: 'none',
    transform: 'rotate(-6deg)',
  },
  heroContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxWidth: '680px',
  },
  heroEyebrow: {
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.veracityOrange,
    fontSize: '11px',
  },
  heroTitle: {
    color: veracityColors.midnightCharcoal,
    fontSize: '32px',
    lineHeight: '40px',
  },
  heroTagline: {
    color: veracityColors.duskBlue,
    fontSize: '13px',
  },
  // --- Sections -------------------------------------------------------------
  section: {
    marginBottom: tokens.spacingVerticalL,
  },
  sectionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.duskBlue,
    fontSize: '11px',
    marginBottom: tokens.spacingVerticalS,
  },
  sectionDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(252px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  // --- Tiles ----------------------------------------------------------------
  //
  // Every tile is the same height, everywhere on the page (Sasha, 2026-08-14).
  // Two things were making them ragged:
  //
  //   1. The grid item is the <Link>, not the Card. `align-items: stretch`
  //      stretched the LINK to the row height while the Card inside stayed at
  //      its content height, so a tile carrying a stat line stood taller than
  //      its neighbours. Both now fill the cell.
  //   2. Even stretched, rows differed from each other, because a section whose
  //      tiles all lack a stat line has a shorter natural row. So the tile
  //      RESERVES both variable rows: the description always occupies two
  //      lines, and the stat row is always rendered even when empty.
  //
  // Reserving beats a magic min-height: the tallest possible tile is the
  // natural height, so it stays correct if the type ramp or the spacing tokens
  // move underneath it.
  tile: {
    position: 'relative',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    borderRadius: '12px',
    border: '1px solid #f0dcc4',
    transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow8,
      border: `1px solid ${veracityColors.gentleHaze}`,
    },
    ':hover .vera-tile-arrow': { opacity: 1, transform: 'translate(2px, -2px)' },
    ':focus-within .vera-tile-arrow': { opacity: 1, transform: 'translate(2px, -2px)' },
  },
  tileLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    height: '100%',
  },
  /** Disabled ("Soon") tiles are not links; they still have to fill the cell. */
  tileSlot: {
    height: '100%',
  },
  tileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  iconCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconGlyph: {
    fontSize: '16px',
    color: '#FFFFFF',
  },
  tileName: {
    color: veracityColors.midnightCharcoal,
    fontSize: '15px',
    lineHeight: '20px',
    fontWeight: tokens.fontWeightSemibold,
  },
  tileDescription: {
    color: veracityColors.duskBlue,
    fontSize: '12px',
    lineHeight: '16px',
    // Always two lines tall: clamped when the copy is longer, reserved when it
    // is shorter. A one-line description must not shorten the tile.
    minHeight: '32px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  // Rendered on EVERY tile, empty or not, and pinned to the bottom, so the
  // stat baseline lines up across a row and tiles without a stat stay the same
  // height as tiles with one.
  tileStat: {
    marginTop: 'auto',
    paddingTop: tokens.spacingVerticalXS,
    minHeight: '16px',
    fontSize: '12px',
    lineHeight: '16px',
    fontFamily: "'JetBrains Mono', monospace",
    color: veracityColors.midnightCharcoal,
    fontWeight: tokens.fontWeightSemibold,
  },
  ctaArrow: {
    position: 'absolute',
    top: tokens.spacingVerticalM,
    right: tokens.spacingHorizontalM,
    fontSize: '16px',
    color: veracityColors.veracityOrange,
    opacity: 0,
    transition: 'opacity 140ms ease, transform 140ms ease',
    pointerEvents: 'none',
  },
  cornerBadge: {
    position: 'absolute',
    top: tokens.spacingVerticalS,
    right: tokens.spacingHorizontalS,
  },
  emptyState: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: veracityColors.duskBlue,
  },
});

export function LauncherPage() {
  const styles = useStyles();
  const { user, roles } = useAuth();
  const firstName = user?.name?.split(' ')[0] ?? '';
  const isGlobalAdmin = hasGlobalAdmin(roles);
  // Demo Pages managers see the Administration tile too — AdminLayout routes
  // them straight to (and only to) the Demo Pages tab.
  const showAdminTile = isGlobalAdmin || canManageDemoPages(roles);

  const launcherCfg = useLauncherCategories();
  const statsQuery = useLauncherStats();

  // The Administration tile is rendered by hand inside its section
  // (role-dependent copy + Admin badge) — keep tool entries out of the map.
  const visibleModules = useAccessibleModules().filter(({ module }) => !module.tool);

  const hasLiveAccess = visibleModules.some(
    ({ module, hasAccess }) => module.status === 'live' && hasAccess
  );

  // Section list from config, with a synthetic catch-all appended when an
  // assignment points at a category an admin has since removed.
  const categories: LauncherCategoryConfig[] = [...launcherCfg.categories];
  const categoryIds = new Set(categories.map((c) => c.id));
  const fallbackCategory: LauncherCategoryConfig = {
    id: '__other',
    label: 'More',
    color: veracityColors.duskBlue,
  };

  const categoryOf = (moduleId: string, builtIn: string): string => {
    const assigned = launcherCfg.assignments[moduleId] ?? builtIn;
    if (categoryIds.has(assigned)) return assigned;
    if (categoryIds.has(builtIn)) return builtIn;
    return fallbackCategory.id;
  };

  const byCategory = new Map<string, AccessibleModule[]>();
  for (const entry of visibleModules) {
    const cid = categoryOf(entry.module.id, entry.module.category);
    const list = byCategory.get(cid);
    if (list) list.push(entry);
    else byCategory.set(cid, [entry]);
  }
  const adminCategoryId = categoryOf('admin', 'tools');
  if (byCategory.has(fallbackCategory.id) || adminCategoryId === fallbackCategory.id) {
    categories.push(fallbackCategory);
  }

  // No longer varies by section: the category color lives on the section label
  // and each tile wears its own module accent.
  const renderTile = () => {
    return function tile({ module: m }: AccessibleModule) {
      // Each tile wears its OWN locked module color (Sasha, 2026-08-08),
      // reverting the 8/08 pass that painted every circle in a section with the
      // category color. Two reasons: the brand guide forbids custom tints and
      // composites, so there was no compliant way to vary hue WITHIN a category
      // and the flat look was the only option left; and a tile now matches the
      // AppShell header of the app it opens into, which the flattening broke.
      // The category color still owns the section chrome below.
      //
      // An admin override (Catalog > Configuration > Launcher categories) wins
      // over the locked accent. The glyph foreground is then DERIVED by
      // luminance rather than read from `accentForeground`, because that field
      // describes the code default and would be wrong the moment an admin
      // switches a dark module to a light color. Deriving keeps brand guide 4.5
      // satisfied for whatever they pick.
      const accent = launcherCfg.moduleColors?.[m.id] ?? m.accent;
      const glyphColor = isLightColor(accent)
        ? veracityColors.midnightCharcoal
        : '#FFFFFF';
      const Icon = m.icon;
      const isDisabled = m.status === 'soon';
      const statLine = statLineFor(m.id, statsQuery.data);

      const inner = (
        <Card className={styles.tile} style={isDisabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}>
          {m.status === 'soon' ? (
            <Badge appearance="outline" color="informative" className={styles.cornerBadge}>
              Soon
            </Badge>
          ) : (
            <ArrowUpRightRegular className={mergeClasses(styles.ctaArrow, 'vera-tile-arrow')} />
          )}
          <div className={styles.tileHeader}>
            <div className={styles.iconCircle} style={{ backgroundColor: accent }}>
              <Icon className={styles.iconGlyph} style={{ color: glyphColor }} />
            </div>
            <span className={styles.tileName}>{m.name}</span>
          </div>
          <div className={styles.tileDescription}>{m.description}</div>
          {/* Always rendered: the empty row is what keeps a stat-less tile the
              same height as its neighbours. */}
          <div className={styles.tileStat}>{statLine ?? ''}</div>
        </Card>
      );

      if (isDisabled) {
        return (
          <div key={m.id} className={styles.tileSlot}>
            {inner}
          </div>
        );
      }
      return (
        <Link key={m.id} to={m.path} className={styles.tileLink}>
          {inner}
        </Link>
      );
    };
  };

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <DaybreakMark
          height={140}
          color={veracityColors.veracityOrange}
          className={styles.heroWatermark}
        />
        <div className={styles.heroContent}>
          <Caption1 className={styles.heroEyebrow}>OneVeracity</Caption1>
          <Display className={styles.heroTitle}>
            {greetingForNow()}{firstName ? `, ${firstName}` : ''}
          </Display>
          <Body1 className={styles.heroTagline}>
            Enhance care. Reduce costs. Make it easy.
          </Body1>
        </div>
      </div>

      {!hasLiveAccess && !isGlobalAdmin && (
        <div className={styles.section}>
          <div className={styles.emptyState}>
            <Body1>
              You don't currently have access to any Vera modules. Ask a Vera
              admin to assign you the appropriate role.
            </Body1>
          </div>
        </div>
      )}

      {categories.map((category) => {
        const entries = byCategory.get(category.id) ?? [];
        const showAdminHere = category.id === adminCategoryId && showAdminTile;
        if (entries.length === 0 && !showAdminHere) return null;
        return (
          <div key={category.id} className={styles.section}>
            <div className={styles.sectionLabel}>
              <span className={styles.sectionDot} style={{ backgroundColor: category.color }} />
              {category.label}
            </div>
            <div className={styles.grid}>
              {entries.map(renderTile())}

              {/* Administration keeps its hand-built tile: role-dependent
                  copy and the Admin badge. */}
              {showAdminHere && (
                <Link to="/admin" className={styles.tileLink}>
                  <Card className={styles.tile}>
                    <Badge
                      appearance="tint"
                      color="brand"
                      icon={<LockClosedRegular />}
                      className={styles.cornerBadge}
                    >
                      Admin
                    </Badge>
                    <div className={styles.tileHeader}>
                      <div
                        className={styles.iconCircle}
                        // Administration's own locked accent, matching the
                        // catalog-driven tiles above rather than the section.
                        style={{ backgroundColor: veracityColors.midnightCharcoal }}
                      >
                        <ShieldKeyholeRegular
                          className={styles.iconGlyph}
                          style={{ color: '#FFFFFF' }}
                        />
                      </div>
                      <span className={styles.tileName}>Administration</span>
                    </div>
                    <div className={styles.tileDescription}>
                      {isGlobalAdmin
                        ? 'Role assignments, user activity, audit log, and public demo pages.'
                        : 'Manage public demo pages.'}
                    </div>
                    {/* Administration carries no stat, but it still reserves the
                        row so it matches the catalog-driven tiles beside it. */}
                    <div className={styles.tileStat} />
                  </Card>
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
